import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/api';
import './QAViewModal.css';

interface QAQuestion {
  id: string;
  dimension_name: string;
  dimension_order?: number;
  component_name: string;
  component_order?: number;
  attribute_name: string;
  attribute_order?: number;
  question_text: string;
  answer_text: string;
  answer_score?: number;
  assessment_status: string;
}

interface QAViewModalProps {
  applicationId: string;
  applicationName: string;
  aprRunId?: string;
  isOpen: boolean;
  onClose: () => void;
}

const QAViewModal: React.FC<QAViewModalProps> = ({
  applicationId,
  applicationName,
  aprRunId,
  isOpen,
  onClose
}) => {
  const [questions, setQuestions] = useState<QAQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadQAData();
    }
  }, [isOpen, applicationId]);

  const loadQAData = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `/api/assessments/qa/${applicationId}`;
      if (aprRunId) {
        url += `?apr_run_id=${encodeURIComponent(aprRunId)}`;
      }
      console.log('QAViewModal: Loading Q&A data', { applicationId, aprRunId, url });
      const response = await authFetch(url);
      console.log('QAViewModal: Response status', response.status, response.ok);
      if (!response.ok) {
        throw new Error('Failed to load Q&A data');
      }
      const data = await response.json();
      setQuestions(data.sort((a: QAQuestion, b: QAQuestion) => {
        const aDimensionOrder = a.dimension_order || 0;
        const bDimensionOrder = b.dimension_order || 0;
        if (aDimensionOrder !== bDimensionOrder) {
          return aDimensionOrder - bDimensionOrder;
        }
        
        const aComponentOrder = a.component_order || 0;
        const bComponentOrder = b.component_order || 0;
        if (aComponentOrder !== bComponentOrder) {
          return aComponentOrder - bComponentOrder;
        }
        
        const aAttributeOrder = a.attribute_order || 0;
        const bAttributeOrder = b.attribute_order || 0;
        if (aAttributeOrder !== bAttributeOrder) {
          return aAttributeOrder - bAttributeOrder;
        }
        
        return a.attribute_name.localeCompare(b.attribute_name);
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to load Q&A data');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const completedCount = questions.filter(q => q.assessment_status === 'Completed').length;
  const completionPercentage = questions.length > 0 
    ? Math.round((completedCount / questions.length) * 100)
    : 0;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2)',
          maxWidth: '95vw',
          maxHeight: '90vh',
          width: '1200px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 1000000
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600', color: '#1976d2' }}>
              Q&A Assessment Details
            </h2>
            <div style={{ fontSize: '16px', color: '#666' }}>
              <span style={{ fontWeight: '500', color: '#333' }}>{applicationName}</span>
              <span style={{ marginLeft: '16px' }}>
                {completedCount}/{questions.length} answered ({completionPercentage}%)
              </span>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              color: '#666'
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1, 
          overflow: 'auto',
          padding: '16px'
        }}>
          {loading ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '300px',
              color: '#666'
            }}>
              <div style={{ marginBottom: '16px', fontSize: '18px' }}>Loading Assessment Data...</div>
              <div>Please wait while we fetch the Q&A assessment details.</div>
            </div>
          ) : error ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '300px',
              color: '#d32f2f'
            }}>
              <div style={{ marginBottom: '16px', fontSize: '18px' }}>Error Loading Data</div>
              <div style={{ marginBottom: '16px' }}>{error}</div>
              <button 
                onClick={loadQAData}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          ) : questions.length === 0 ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '300px',
              color: '#666'
            }}>
              <div style={{ marginBottom: '16px', fontSize: '18px' }}>No Q&A Data Available</div>
              <div>This application has no Q&A assessment data available.</div>
            </div>
          ) : (
            <div>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: '600',
                      width: '5%'
                    }}>#</th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: '600',
                      width: '15%'
                    }}>Dimension</th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: '600',
                      width: '15%'
                    }}>Component</th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: '600',
                      width: '15%'
                    }}>Attribute</th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: '600',
                      width: '30%'
                    }}>Question</th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: '600',
                      width: '10%'
                    }}>Score</th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: '600',
                      width: '10%'
                    }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((question, index) => (
                    <tr key={question.id} style={{ 
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                    }}>
                      <td style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>
                        {index + 1}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <span style={{ 
                          backgroundColor: '#e3f2fd', 
                          color: '#1976d2', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {question.dimension_name}
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <span style={{ 
                          backgroundColor: '#f3e5f5', 
                          color: '#7b1fa2', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {question.component_name}
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <span style={{ 
                          backgroundColor: '#e8f5e8', 
                          color: '#2e7d32', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {question.attribute_name}
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px', fontSize: '13px', lineHeight: '1.4' }}>
                        {question.question_text}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        {question.assessment_status === 'Completed' && question.answer_score ? (
                          <span style={{ 
                            backgroundColor: question.answer_score >= 4 ? '#c8e6c9' : question.answer_score >= 3 ? '#fff3e0' : '#ffcdd2',
                            color: question.answer_score >= 4 ? '#2e7d32' : question.answer_score >= 3 ? '#f57c00' : '#d32f2f',
                            padding: '4px 8px', 
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {question.answer_score.toFixed(1)}
                          </span>
                        ) : (
                          <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <span style={{ 
                          backgroundColor: question.assessment_status === 'Completed' ? '#c8e6c9' : '#ffecb3',
                          color: question.assessment_status === 'Completed' ? '#2e7d32' : '#f57c00',
                          padding: '4px 8px', 
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {question.assessment_status === 'Completed' ? '✓ Answered' : '⏳ Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', gap: '24px', fontSize: '14px', color: '#666' }}>
            <div>
              <span style={{ fontWeight: '600', color: '#333' }}>{questions.length}</span> Total Questions
            </div>
            <div>
              <span style={{ fontWeight: '600', color: '#333' }}>{completedCount}</span> Answered
            </div>
            <div>
              <span style={{ fontWeight: '600', color: '#333' }}>{completionPercentage}%</span> Complete
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{
              padding: '8px 24px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default QAViewModal;